import { formatPerson } from "@/utils/helper";
import { useEffect, useMemo, useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  useGetScopeEmployeesQuery,
  type IGetScopeEmployeesParams,
  type IScopeEmployee,
} from "@/services/(company)/payrollRunsApi";
import type { ScopeItem } from "./scope-data";
import { useTranslation } from "react-i18next";

interface IProps {
  selected: ScopeItem[];
}

const PAGE_SIZE = 20;

const getInitials = (first?: string, last?: string) => {
  const a = first?.trim()?.[0] ?? "";
  const b = last?.trim()?.[0] ?? "";
  const initials = `${a}${b}`.toUpperCase();
  return initials || "?";
};

const buildScopeParams = (
  selected: ScopeItem[],
): Omit<IGetScopeEmployeesParams, "page" | "limit"> | null => {
  if (selected.length === 0) return null;

  const branchItem = selected.find((s) => s.kind === "branch");
  const branchStringId =
    branchItem?.id ?? selected.find((s) => s.parentBranchId)?.parentBranchId;
  if (!branchStringId) return null;

  const branchId = Number(branchStringId);

  if (branchItem && selected.length === 1) return { branchId };

  const departmentIds = selected
    .filter((s) => s.kind === "department")
    .map((s) => Number(s.id.replace("dept-", "")));
  const payrollGroupIds = selected
    .filter((s) => s.kind === "group")
    .map((s) => Number(s.id.replace("grp-", "")));

  return {
    branchId,
    ...(departmentIds.length ? { departmentIds } : {}),
    ...(payrollGroupIds.length ? { payrollGroupIds } : {}),
  };
};

export const SelectionPreview = ({ selected }: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const scopeParams = useMemo(() => buildScopeParams(selected), [selected]);
  const scopeKey = useMemo(() => JSON.stringify(scopeParams), [scopeParams]);

  const [page, setPage] = useState(1);
  const [items, setItems] = useState<IScopeEmployee[]>([]);
  const [scopeTotal, setScopeTotal] = useState(0);

  useEffect(() => {
    setPage(1);
    setItems([]);
    setScopeTotal(0);
  }, [scopeKey]);

  const { currentData, isFetching, isLoading } = useGetScopeEmployeesQuery(
    scopeParams
      ? { ...scopeParams, page, limit: PAGE_SIZE }
      : ({} as IGetScopeEmployeesParams),
    { skip: !scopeParams },
  );

  useEffect(() => {
    if (!currentData?.data) return;
    setItems((prev) =>
      page === 1 ? currentData.data : [...prev, ...currentData.data],
    );
    const meta = currentData.meta;
    const next =
      meta?.totalItems ??
      meta?.totalRecords ??
      meta?.total ??
      currentData.data.length;
    setScopeTotal(next);
  }, [currentData, page]);

  const totalEmployees = useMemo(() => {
    if (scopeTotal) return scopeTotal;
    if (selected.some((s) => s.kind === "branch")) {
      return selected.find((s) => s.kind === "branch")?.employees ?? 0;
    }
    return selected.reduce((acc, s) => acc + s.employees, 0);
  }, [selected, scopeTotal]);

  const hasMore = items.length < scopeTotal;
  const isLoadingMore = isFetching && page > 1;
  const isInitialLoading = isLoading && page === 1;

  return (
    <aside className="flex flex-col gap-3 h-full">
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
        {t("company:payrolls.selection_preview")}
      </span>

      {!scopeParams ? (
        <div className="rounded-xs border border-border bg-white flex-1 flex items-center justify-center text-xs text-gray-400 px-4 py-6 text-center">
          {t(
            "company:payrolls.select_a_branch_or_its_departments_custom_groups_to_prev",
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2 flex-1 min-h-0">
          <div className="flex items-center justify-between w-full rounded-xs border border-border bg-white px-4 py-3 text-sm font-semibold text-gray-900">
            <span>{t("company:payrolls.employees")}</span>
            <span>
              {t("company:payrolls.employee_count", { count: totalEmployees })}
            </span>
          </div>

          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-1">
            {t("company:payrolls.employees_included")}
          </span>

          <div className="flex flex-col gap-2 overflow-auto pe-1 max-h-[620px]">
            {isInitialLoading ? (
              <div className="text-xs text-gray-400 py-4 text-center">
                {t("company:payrolls.loading_employees")}
              </div>
            ) : items.length === 0 ? (
              <div className="text-xs text-gray-400 py-4 text-center">
                {t("company:payrolls.no_employees_in_this_scope")}
              </div>
            ) : (
              <>
                {items.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center gap-3 rounded-xs border border-border bg-white px-3 py-2"
                  >
                    <Avatar className="size-8">
                      {e.profileImage && (
                        <AvatarImage
                          src={e.profileImage}
                          alt={formatPerson(e)}
                        />
                      )}
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                        {getInitials(e.firstName, e.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-gray-900 truncate">
                        {e.firstName} {e.lastName}
                      </span>
                      <span className="text-[11px] text-gray-400 truncate">
                        {e.employeeId}
                        {e.department?.name && (
                          <>
                            <span className="text-gray-300"> | </span>
                            {e.department.name}
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                ))}

                {hasMore && (
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={isLoadingMore}
                    className="self-center mt-1 text-xs font-semibold text-primary hover:underline disabled:opacity-50 disabled:no-underline"
                  >
                    {isLoadingMore
                      ? t("common:states.loading")
                      : t("company:payrolls.load_more")}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </aside>
  );
};
